import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useChatContext } from "@/contexts/ChatContext";
import { notifySound } from "@/lib/notifySound";
import { soundKeyForType } from "@/lib/soundEventMap";
import { subscribeNotificationInsert } from "@/lib/notificationsBus";
import { showWebNotification } from "@/lib/webNotifications";

/**
 * Single global in-app alert channel for the logged-in user.
 * Subscribes (via the shared notifications bus) to INSERTs on `notifications`
 * filtered by recipient_user_id, plays the mapped sound, and shows a sonner
 * toast for EVERY notification type — regardless of account role. Per-role
 * hooks own their badge counters and query invalidation but no longer toast
 * or chime on the same INSERT (would double-alert).
 *
 * The notification.type → sound mapping now lives in `@/lib/soundEventMap`
 * and is user-configurable from Sound Notification settings.
 */

function soundKeyFor(type: string | undefined | null): string {
  return soundKeyForType(type);
}


// Minimum gap between two chimes. A burst of 10 bids becomes 1 ding, not 10.
const CHIME_MIN_GAP_MS = 1500;

export function useGlobalNotificationAlerts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openChats, conversations } = useChatContext();
  const seenIdsRef = useRef<Set<string>>(new Set());
  const lastChimeAtRef = useRef<number>(0);

  // Keep latest dock state readable inside the subscribe callback without
  // re-subscribing on every render. "On the messages view" = at least one
  // chat window is open in the floating dock AND not minimized.
  const isOnMessagesViewRef = useRef(false);
  isOnMessagesViewRef.current = openChats.some((id) => {
    const c = conversations.find((cv) => cv.id === id);
    return c ? !c.isMinimized : true;
  });

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;


    const off = subscribeNotificationInsert(userId, (row) => {
      const r = row as {
        id?: string;
        type?: string;
        title?: string;
        message?: string;
        action_url?: string;
      };

      // Dedupe against React StrictMode double-mount / overlapping listeners.
      if (r?.id) {
        if (seenIdsRef.current.has(r.id)) return;
        seenIdsRef.current.add(r.id);
        if (seenIdsRef.current.size > 500) {
          const arr = Array.from(seenIdsRef.current);
          seenIdsRef.current = new Set(arr.slice(-250));
        }
      }

      // Refresh anything keyed on notifications for this user.
      queryClient.invalidateQueries({ queryKey: ["notifications", userId] });

      // DM suppression: if the user has the floating messenger dock open on
      // any non-minimized conversation, they're actively looking at messages.
      // Fully silent for DMs — no toast, no chime, no OS popup. The dock
      // itself surfaces unread on other threads (bold row + badge + rise-to-
      // top ordering), so a DM to a different conversation stays visible.
      const isDm = r?.type === "dm_received";
      if (isDm && isOnMessagesViewRef.current) {
        return;
      }

      // Chime (respects sound settings inside notifySound). Debounce bursts.
      const now = Date.now();
      if (now - lastChimeAtRef.current >= CHIME_MIN_GAP_MS) {
        lastChimeAtRef.current = now;
        notifySound.play(soundKeyFor(r?.type));
      }

      // Toast.
      const title = r?.title ?? "New notification";
      const description = r?.message ?? undefined;
      const actionUrl = r?.action_url;

      if (isDm) {
        // DMs have no clean in-app destination (messenger is a floating
        // dock, not a route). Show a simple, quick, dismissable toast —
        // no View action, no OS-notification navigate.
        toast(title, {
          description,
          duration: 4000,
          closeButton: true,
        });
        showWebNotification({
          title,
          body: description,
          tag: r?.id,
        });
      } else {
        toast.success(title, {
          description,
          duration: 10000,
          ...(actionUrl
            ? {
                action: {
                  label: "View",
                  onClick: () => navigate(actionUrl),
                },
              }
            : {}),
        });
        // OS-level notification when the tab is not focused. showWebNotification
        // no-ops on native, when permission isn't granted, and when the tab is
        // already focused — so users don't get a duplicate on top of the toast.
        showWebNotification({
          title,
          body: description,
          tag: r?.id,
          onClick: actionUrl ? () => navigate(actionUrl) : undefined,
        });
      }
    });

    return off;
  }, [user?.id, navigate, queryClient]);
}
